import { Input, Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import {
  trackScsPageViewed,
  trackScsCountrySelected,
  trackScsStoreSelected,
  trackScsRegionSelected,
  trackScsConfigFetched,
  trackScsConfigFetchError,
  trackScsKeySearched,
  trackScsConfigSaved,
  trackScsConfigSaveError,
  trackScsCreateKeyOverlayOpened,
  trackScsCreateKeyOverlayClosed,
  trackScsKeyCreated,
  trackScsKeyCreationError,
  trackScsScopeDiffCheckBulkUpdateSuccess,
  trackScsScopeDiffCheckBulkUpdateError,
  getEditDuration,
} from '../../utils/analytics';
import { formatStoreAlias } from '../../utils/formatting';
import { loadState } from '../../utils/local-storage';
import { getStores } from '../../utils/service-calls/reusable';
import {
  scsGet, scsDefinitionPost, scsDefinitionGet, scsPost, scsBulkedit, scsSchedulePost,
} from '../../utils/service-calls/scs';
import { isUserAllowed } from '../../utils/tab-permissions';
import Modal from '../ccm/Modal';
import {
 StoreSelect, Select, ButtonBlack,
} from '../reusable';

import Card from './Card';
import './config-by-key/Overlay.css';
import CreateConfigOverlay from './CreateConfigOverlay';

const config = require('../../config')();

const otherSettingsGroup = 'Other Settings';

// if we don't get the correct security group we don't show that card
const securityGroupMap = new Map([
  ['POS Settings', ['SCS.POS', 'SCS.POS.ReadOnly']],
  ['AIC Settings', ['SCS.ASSIST', 'SCS.ASSIST.ReadOnly']],
  ['Assist Settings', ['SCS.ASSIST', 'SCS.ASSIST.ReadOnly']],
  ['Nike+ Link Settings', ['SCS.LINK', 'SCS.LINK.ReadOnly']],
  ['NSP Settings', ['SCS.ASSIST', 'SCS.ASSIST.ReadOnly']],
  ['RISE Settings', ['SCS.RISE', 'SCS.RISE.ReadOnly']],
  ['SCORE Settings', ['SCS.SFS', 'SCS.SFS.ReadOnly']],
  ['SIM Settings', ['SCS.SIM', 'SCS.SIM.ReadOnly']],
  ['Self Checkout Settings', ['SCS.SCO', 'SCS.SCO.ReadOnly']],
  ['NPOS Settings', ['SCS.NPOS', 'SCS.NPOS.ReadOnly']],
  ['Retail Launchpad', ['SCS.RL', 'SCS.RL.ReadOnly']],
  ['Promote App Settings', ['SCS.PROMOTE', 'SCS.PROMOTE.ReadOnly']],
  ['Other Settings', ['SCS.OTHER', 'SCS.OTHER.ReadOnly']],
  ['Track Settings', ['SCS.TRACK', 'SCS.TRACK.ReadOnly']],
]);

const scsKeyGroups = ['SCS.ALL', 'SCS.ALL.ReadOnly', 'SCS.ASSIST', 'SCS.ASSIST.ReadOnly', 'SCS.LINK', 'SCS.LINK.ReadOnly', 'SCS.NPOS', 'SCS.NPOS.ReadOnly', 'SCS.OTHER', 'SCS.OTHER.ReadOnly', 'SCS.POS', 'SCS.POS.ReadOnly', 'SCS.PROMOTE', 'SCS.PROMOTE.ReadOnly', 'SCS.RL', 'SCS.RL.ReadOnly', 'SCS.RISE', 'SCS.RISE.ReadOnly', 'SCS.SFS', 'SCS.SFS.ReadOnly', 'SCS.SIM', 'SCS.SIM.ReadOnly', 'SCS.SCO', 'SCS.SCO.ReadOnly', 'SCS.TRACK', 'SCS.TRACK.ReadOnly'];

const storeviewsFields = ['address.country', 'brand', 'id', 'storeNumber', 'region'];

const getKey = ({ key }) => key;

const getIncludedKeysFrom = ({ keys }) => ({ key: settingKey }) => keys.map(getKey).includes(settingKey);

const getSearchedKeys = (search, keyConfigs) => keyConfigs.filter((keyConfig) => {
  if (search !== '') {
    const regExp = new RegExp(search.trim(), 'i');
    return regExp.test(String(keyConfig.key));
  } else {
    return true;
  }
});

export const getCurrentConfigForCard = (storeConfig, card) => storeConfig.filter(getIncludedKeysFrom(card));

class SCSMain extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      config: [],
      errorMessage: '',
      filteredGroup: [],
      getting: false,
      isMessageModalOpen: false,
      isMessageSuccess: false,
      isOverlayVisible: false,
      isSaving: false,
      keyCreationDefinitions: [],
      keyDefinitions: [],
      keySaveMessage: '',
      messageError: '',
      saveSuccess: false,
      scheduleSuccess: false,
      scsGroups: [],
      search: '',
      selectedCountry: loadState('auth')?.country,
      selectedRegion: null,
      selectedStore: null,
      storeKey: '',
      stores: [],
      totalStoresUpdated: 0,
      updateKey: '',
    };

    this.overlayRef = React.createRef();
    this.searchTimeout = null;
  }

  async componentDidMount() {
    if (this.state.scsGroups.length === 0) {
      const isReadOnly = isUserAllowed(this.props.userInfo.groups, 'ReadOnly');
      const userScsGroups = scsKeyGroups.filter((group) => isUserAllowed(this.props.userInfo.groups, group));

      this.setState({
        isReadOnly,
        scsGroups: userScsGroups,
      });

      // Track page view
      trackScsPageViewed({
        isReadOnly,
        userGroups: this.props.userInfo.groups,
      });

      const storeFields = [
        'id',
        'storeNumber',
        'name',
        'address.country',
        'region'
      ];
      const fieldQuery = storeFields.join(',');
      try {
        const response = await getStores(fieldQuery);
        this.setState({ stores: response });
      } catch (err) {
        console.error('Error fetching stores:', err);
        this.setState({ stores: [] });
      }
    }
  }

  componentWillUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  closeMessageModal = () => {
    this.setState({ isMessageModalOpen: false });
  };

  closeOverlay = () => {
    trackScsCreateKeyOverlayClosed({ completed: false });
    this.setState({ errorMessage: '', filteredGroup: [], isOverlayVisible: false });
  };

  determineReadOnlyForCard = (cardSecurityGroup) => {
    const writeSecurityKey = cardSecurityGroup.filter((group) => !group.includes('ReadOnly'));
    const writeAccess = this.state.scsGroups.includes('SCS.ALL') || this.state.scsGroups.some((group) => group.includes(writeSecurityKey));
    return !writeAccess;
  }

  getSCS = () => {
    const startTime = Date.now();
    this.setState({ getting: true },
      () => scsGet(this.props.userInfo.accessToken, this.state.storeKey, this.state.selectedRegion)
        .then((res) => this.setState({ config: res.body.objects }, async () => {
          const keyDefinitions = await this.getSCSKeyDefinitions();
          const loadTimeMs = Date.now() - startTime;

          // Track successful config fetch
          trackScsConfigFetched({
            configCount: res.body.objects?.length || 0,
            loadTimeMs,
            region: this.state.selectedRegion,
            storeId: this.state.selectedStore?.id,
          });

          this.setState(({ getting: false, keyDefinitions }));
        }))
        .catch((err) => {
          // Track config fetch error
          trackScsConfigFetchError({
            errorMessage: err.message,
            region: this.state.selectedRegion,
            storeId: this.state.selectedStore?.id,
          });
          this.setState({ config: [], getting: false, messageError: err.message });
        }));
  };

  getSCSKeyDefinitions = async () => {
    const data = await scsDefinitionGet(this.props.userInfo.accessToken, this.state.selectedRegion);
    const { objects: definitions } = data?.body;
    const groupedItems = definitions.reduce((accumulator, item) => {
      const { group } = item;
      const relevantGroup = !group ? otherSettingsGroup : group;
      if (!accumulator[relevantGroup]) {
        accumulator[relevantGroup] = [];
      }

      // creating key of the format { description: < key description>, key: <key name>, valueType: <key value type> }
      const keyConfig = {
        description: !item.description ? '' : item.description,
        key: item.key,
        valueType: String(item.type).toLowerCase(),
      };
      accumulator[relevantGroup].push(keyConfig);
      return accumulator;
    }, {});

    // creating scs card info based on scs key groups of the format { header: 'group name', keys: <group keys>, securityGroup: 'security group to manage group keys' }
    const scsCardsInfo = Object.entries(groupedItems).sort().map(([key, value]) => ({
      header: key,
      keys: value,
      securityGroup: securityGroupMap.get(key),
    }));
    return scsCardsInfo;
  }

  // Function called from CreateConfigOverlay to get the key definitions and security group for the selected region
  handleOverlayAction = async (selectedRegion) => {
    this.setState({ selectedRegion }, async () => {
        try {
          const keyDefinitions = await this.getSCSKeyDefinitions();
          this.setState({ keyCreationDefinitions: keyDefinitions }, () => {
            const headerSecurityGroupMap = this.state.keyCreationDefinitions.reduce((acc, definition) => {
              acc[definition.header] = definition.securityGroup;
              return acc;
            }, {});
            const securityGroupFilter = (securityGroup) => {
              const writeSecurityKey = securityGroup.filter((group) => !group.includes('ReadOnly'));
              return this.state.scsGroups.includes('SCS.ALL') || this.state.scsGroups.includes(writeSecurityKey);
            };
            const filteredGroup = Object.entries(headerSecurityGroupMap)
              .filter(([, securityGroup]) => securityGroupFilter(securityGroup))
              .reduce((acc, [header, securityGroup]) => {
                acc[header] = securityGroup;
                return acc;
              }, {});

            const keys = Object.keys(filteredGroup);
            this.setState({ filteredGroup: keys });
          });
        } catch (error) {
          console.error('Error fetching key definitions:', error);
        }
    });
  };

  handleSaveKeyDefinition = async (decription, group, key, type, value, isCreateGlobalConfig, isSaveAndClose) => {
    try {
      // Clear previous error/success messages and set saving state
      this.setState({ errorMessage: '', isSaving: true, keySaveMessage: '' });
      await scsDefinitionPost(this.props.userInfo.accessToken, decription, group, key, type, this.state.selectedRegion);
      // If the key is created successfully, and the isCreateGlobalConfig is true, then create the global scoped property
      if (isCreateGlobalConfig) {
        await scsPost(this.props.userInfo.accessToken, {
            changeRequest: {
              justification: 'Initial global scope value during key creation',
              requestedBy: this.props.userInfo?.email || '',
            },
            key,
            scope: 'GLOBAL',
            scopeType: 'GLOBAL',
            value
            }, this.state.selectedRegion);
      }

      // Track successful key creation
      trackScsKeyCreated({
        group,
        hasGlobalValue: isCreateGlobalConfig,
        keyName: key,
        region: this.state.selectedRegion,
        userEmail: this.props.userInfo.email,
        valueType: type,
      });

      this.clearChildFields();

      if (isSaveAndClose) {
        // Close overlay and show success modal
        trackScsCreateKeyOverlayClosed({ completed: true });
        this.setState({
          errorMessage: '',
          filteredGroup: [],
          isMessageModalOpen: true,
          isMessageSuccess: true,
          isOverlayVisible: false,
          isSaving: false,
          keySaveMessage: 'Key successfully created',
        });
      } else {
        // Save & New: Keep overlay open with filteredGroup intact, show brief success message
        this.setState({
          errorMessage: '',
          isSaving: false,
          keySaveMessage: 'Key successfully created',
        });
        // Show a brief inline success notification (will be cleared on next interaction)
        setTimeout(() => {
          this.setState({ keySaveMessage: '' });
        }, 3000);
      }
    } catch (error) {
      // Extract error message safely
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      const errorCode = errorMessage.includes('409') ? '409' : 'unknown';

      // Track key creation error (wrapped in try-catch to ensure state is always reset)
      try {
        trackScsKeyCreationError({
          errorCode,
          errorMessage,
          keyName: key,
          userEmail: this.props.userInfo.email,
        });
      } catch (trackingError) {
        console.error('Error tracking key creation error:', trackingError);
      }

      // Set error state - do NOT clear fields or close overlay so user can see the error and retry
      // Show error within the CreateConfigOverlay, NOT in a separate modal to prevent overlapping modals
      const errorMsg = errorMessage.includes('409')
        ? 'Conflict Error. Key already exists!'
        : `Error creating key: ${errorMessage}`;

      this.setState({
        errorMessage: errorMsg,
        isSaving: false,
        keySaveMessage: '', // Clear any previous success message
      });
    }
  };

  onBulkSave = ({
    body,
    keyName,
    selectedCount,
    totalCount,
  }) => this.setState({ getting: true, messageError: '', saveSuccess: false },
    () => {
      const userEmail = this.props.userInfo.email;
      scsBulkedit(
        this.props.userInfo.accessToken,
        body,
        this.state.selectedRegion
      )
        .then((response) => {
          if (response.body.success && response.body.success.length > 0) {
            trackScsScopeDiffCheckBulkUpdateSuccess({
              keyName,
              selectedCount,
              successCount: response.body.success.length,
              userEmail,
            });
            this.setState({ saveSuccess: true, totalStoresUpdated: totalCount, updateKey: keyName },
              () => this.getSCS());
          } else if (response.body.failure && response.body.failure.length > 0) {
            trackScsScopeDiffCheckBulkUpdateError({
              errorMessage: 'Partial failure - some updates failed',
              keyName,
              selectedCount,
              userEmail,
            });
            this.setState({ getting: false, messageError: `${keyName}: Partial failure - some updates failed` });
          }
          return null;
        })
        .catch((error) => {
          trackScsScopeDiffCheckBulkUpdateError({
            errorMessage: error.message || 'Unknown error',
            keyName,
            selectedCount,
            userEmail,
          });
          this.setState({ getting: false, messageError: `${keyName}: ${error.message || 'Unknown error'}` });
        });
    });

  onSave = (data) => this.setState({ getting: true, messageError: '', saveSuccess: false },
    () => scsPost(this.props.userInfo.accessToken, data.body, this.state.selectedRegion)
      .then(() => {
        // Track successful config save
        trackScsConfigSaved({
          durationMs: getEditDuration(data.body.key),
          hasJustification: !!data.body.changeRequest?.justification,
          keyName: data.body.key,
          region: this.state.selectedRegion,
          scope: data.body.scope,
          scopeType: data.body.scopeType,
          storeId: this.state.selectedStore?.id,
          userEmail: this.props.userInfo.email,
          valueType: data.keyType,
        });
        this.setState({ saveSuccess: true, totalStoresUpdated: data.totalCount, updateKey: data.body.key },
          () => this.getSCS());
        return null;
      })
      // the scope doesn't exist, so you have to post the definition of it and try again
      .catch(() => scsDefinitionPost(this.props.userInfo.accessToken, data.description, data.group, data.body.key, data.keyType, this.state.selectedRegion)
        .then(() => scsPost(this.props.userInfo.accessToken, data.body, this.state.selectedRegion)
          .then(() => {
            // Track successful config save (after definition creation)
            trackScsConfigSaved({
              durationMs: getEditDuration(data.body.key),
              hasJustification: !!data.body.changeRequest?.justification,
              keyName: data.body.key,
              region: this.state.selectedRegion,
              scope: data.body.scope,
              scopeType: data.body.scopeType,
              storeId: this.state.selectedStore?.id,
              userEmail: this.props.userInfo.email,
              valueType: data.keyType,
            });
            this.setState({ saveSuccess: true, updateKey: data.body.key },
              () => this.getSCS());
            return null;
          })
          .catch((err) => {
            trackScsConfigSaveError({
              errorMessage: err.message,
              keyName: data.body.key,
              region: this.state.selectedRegion,
              scopeType: data.body.scopeType,
              storeId: this.state.selectedStore?.id,
              userEmail: this.props.userInfo.email,
            });
            this.setState({ getting: false, messageError: `${data.body.key}: ${err.message}` });
          }))
        .catch((err) => {
          trackScsConfigSaveError({
            errorMessage: err.message,
            keyName: data.body.key,
            region: this.state.selectedRegion,
            scopeType: data.body.scopeType,
            storeId: this.state.selectedStore?.id,
            userEmail: this.props.userInfo.email,
          });
          this.setState({ getting: false, messageError: `${data.body.key}: ${err.message}` });
        })));

  onSchedule = ({
    body,
    keyName,
  }) => this.setState({ getting: true, messageError: '', scheduleSuccess: false },
    () => {
      scsSchedulePost(
        this.props.userInfo.accessToken,
        body,
        this.state.selectedRegion
      )
        .then(() => {
            this.setState({ scheduleSuccess: true, updateKey: keyName },
              () => this.getSCS());
          return null;
        })
        .catch((error) => {
          this.setState({ getting: false, messageError: `${keyName}: ${error.message || 'Unknown error during schedule creation'}` });
        });
    });

  saveConfig = async () => {
    trackScsCreateKeyOverlayOpened();
    this.setState({ isOverlayVisible: true });
  };

  selectCountry = (selectedCountry) => {
    const previousCountry = this.state.selectedCountry;
    trackScsCountrySelected({
      country: selectedCountry?.value || selectedCountry,
      previousCountry: previousCountry?.value || previousCountry,
    });
    this.setState({
      config: [], messageError: '', search: '', selectedCountry, selectedStore: null,
    });
  };

  selectRegion = (selectedRegion) => {
    trackScsRegionSelected({
      region: selectedRegion,
      storeId: this.state.selectedStore?.id,
    });
    this.setState({ config: [], search: '', selectedRegion },
      () => this.getSCS());
  };

  selectStore = (selectedStore) => {
    trackScsStoreSelected({
      brand: selectedStore?.brand,
      country: selectedStore?.address?.country,
      region: selectedStore?.region,
      storeId: selectedStore?.id,
      storeNumber: selectedStore?.storeNumber,
    });
    this.setState({
      config: [], messageError: '', search: '', selectedRegion: null, selectedStore, storeKey: formatStoreAlias(selectedStore),
    });
  };

  updateSearch = (value) => {
    this.setState({ search: value });
    // Debounced search tracking - only track after user stops typing
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      if (value && value.length >= 2) {
        const filteredCount = this.state.keyDefinitions
          .filter((card) => getSearchedKeys(value, card.keys).length > 0)
          .reduce((sum, card) => sum + getSearchedKeys(value, card.keys).length, 0);
        trackScsKeySearched({
          resultsCount: filteredCount,
          searchTerm: value,
          storeId: this.state.selectedStore?.id,
        });
      }
    }, 500);
  };

  clearChildFields() {
      this.overlayRef.current.clearFields(); // Call the clearFields method
  }

  render = () => (
    <main className="ncss-col-sm-10">
      <div className="create-key">
        <ButtonBlack
          className="create-key-button"
          isDisabled={false}
          label="Create Key"
          onClick={this.saveConfig}
        />
      </div>

      {this.state.isReadOnly && <h1 className="headline-1 mb4-sm text-color-accent">Read Only</h1>}

      {this.state.scsGroups.length !== 0
        ? (
          <StoreSelect
            selectCountry={this.selectCountry}
            selectStore={this.selectStore}
            selectedCountry={this.state.selectedCountry}
            selectedStore={this.state.selectedStore}
            storeviewsFields={storeviewsFields}
          />

        )

        : !this.state.getting && <p>You do not have permission to edit any SCS keys.</p>}

      {this.state.saveSuccess
        ? <aside className="m4-sm text-color-success body-2">{this.state.updateKey}: successfully saved. SCS data is cached; changes will be reflected in {this.state.totalStoresUpdated} stores shortly.</aside>
        : <aside className="m4-sm text-color-error body-2">{this.state.messageError}</aside>}
      {this.state.scheduleSuccess
        ? <aside className="m4-sm text-color-success body-2">Changes for {this.state.updateKey} successfully scheduled.</aside>
        : <aside className="m4-sm text-color-error body-2">{this.state.messageError}</aside>}
      {this.state.selectedStore && this.state.selectedStore.id && (
        <header className="ncss-col-sm-6 ta-sm-l">
          <p className="mt4-sm mb2-sm">{`Country: ${this.state.selectedStore.address.country}`}</p>
          <p className="mt4-sm mb2-sm">{`Store Id: ${this.state.selectedStore.id}`}</p>

          <Select
            className="mt4-sm mb4-sm"
            id="scsRegion"
            label="AWS Region"
            options={config.scsRegionOptions}
            value={this.state.selectedRegion}
            onChange={this.selectRegion}
          />
          {this.state.selectedStore && this.state.selectedStore.id && this.state.selectedRegion && !this.state.getting && (
            <section className="ncss-col-sm-12 ta-sm-c p1-sm mt4-sm">
              <Input
                label="Search"
                placeholder="Search for Store Config Key..."
                style={{ zIndex: 1 }}
                type="text"
                value={this.state.search}
                onChange={({ target: { value } }) => this.updateSearch(value)}
              />
            </section>
          )}
        </header>
      )}

      <section className="ncss-row mt8-sm">
        {this.state.getting
          ? <Loading />
          : this.state.config.length !== 0 && (
            this.state.keyDefinitions.filter((card) => this.state.scsGroups.includes('SCS.ALL') || this.state.scsGroups.includes('SCS.ALL.ReadOnly') || card.securityGroup?.some((group) => this.state.scsGroups.includes(group)))
              .filter((card) => getSearchedKeys(this.state.search, card.keys).length > 0)
              .map((card) => (
                <Card
                  key={card.header}
                  accessToken={this.props.userInfo.accessToken}
                  allStores={this.state.stores}
                  currentConfig={getCurrentConfigForCard(this.state.config, card)}
                  header={card.header}
                  isReadOnly={this.determineReadOnlyForCard(card.securityGroup)}
                  keys={getSearchedKeys(this.state.search, card.keys)}
                  scsRegion={this.state.selectedRegion}
                  store={this.state.selectedStore}
                  userInfo={this.props.userInfo}
                  onBulkSave={this.onBulkSave}
                  onSave={this.onSave}
                  onSchedule={this.onSchedule}
                />
              ))
          )}
      </section>
      <div>
        <CreateConfigOverlay
          ref={this.overlayRef}
          errorMessage={this.state.errorMessage}
          filteredGroup={this.state.filteredGroup}
          isSaving={this.state.isSaving}
          isVisible={this.state.isOverlayVisible}
          successMessage={this.state.keySaveMessage}
          onAction={this.handleOverlayAction}
          onClose={this.closeOverlay}
          onSave={this.handleSaveKeyDefinition}
        />
      </div>
      <div>
        <Modal
          buttonName="OK"
          isOpen={this.state.isMessageModalOpen}
          title={this.state.isMessageSuccess ? 'Success' : 'Error'}
          onClose={() => this.closeMessageModal(false)}
        >
          <p>{this.state.keySaveMessage}</p>
        </Modal>
      </div>
    </main>
  )
}

SCSMain.propTypes = {
  userInfo: PropTypes.shape({
    accessToken: PropTypes.string,
    email: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.authorizationReducer.auth,
});

export default connect(mapStateToProps, null)(SCSMain);
